import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../epg/constants/general';
import { getHeaders } from '../../epg/utils/shared/headers';
import { fetchAndCacheState } from '../../Upshow';
import './TestStreamBanner.scss';

export default function TestStreamBanner({ onDismiss }) {
    const [isLoading, setIsLoading] = useState(false);
    const skipRef = useRef(null);
    const isLoadingRef = useRef(false);

    useEffect(() => {
        skipRef.current?.focus();
    }, []);

    const handleSkip = useCallback(async () => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        try {
            await axios.post(`${BASE_URL}/v6/unsubscribe_test_stream`, {}, { headers: getHeaders() });
            if (window.__UpshowState) window.__UpshowState.isSubscribedToTestStream = false;
            fetchAndCacheState();
            onDismiss();
        } catch (err) {
            console.error('TestStreamBanner skip error:', err);
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [onDismiss]);

    // Dismiss when isSubscribedToTestStream flips to false. fetchAndApplyLiveEvents
    // calls fetchAndCacheState when the content goes null while subscribed, so this
    // fires in lockstep with the content switch. 2-second delay avoids a false
    // positive from the fetchAndCacheState fired right after subscribe.
    useEffect(() => {
        let listener = null;
        let wasSubscribed = false;
        const startId = setTimeout(() => {
            listener = (e) => {
                if (e.detail?.isSubscribedToTestStream === true) {
                    wasSubscribed = true;
                } else if (wasSubscribed && e.detail?.isSubscribedToTestStream === false) {
                    onDismiss();
                }
            };
            window.addEventListener('UpshowStateUpdate', listener);
        }, 2000);
        return () => {
            clearTimeout(startId);
            if (listener) window.removeEventListener('UpshowStateUpdate', listener);
        };
    }, [onDismiss]);

    // Keyboard: Enter/Space skips the stream.
    // 300ms delay prevents a lingering key-repeat Enter from VisualTestUpNext
    // from immediately dismissing the banner before the user sees it.
    useEffect(() => {
        let handler = null;
        const startId = setTimeout(() => {
            handler = (e) => {
                if (!['Enter', ' ', 'Return', 'Accept'].includes(e.key)) return;
                e.stopPropagation();
                e.stopImmediatePropagation();
                e.preventDefault();
                handleSkip();
            };
            window.addEventListener('keydown', handler, { capture: true });
        }, 300);
        return () => {
            clearTimeout(startId);
            if (handler) window.removeEventListener('keydown', handler, { capture: true });
        };
    }, [handleSkip]);

    return (
        <div className="TsBanner">
            <p className="TsBanner__text">
                This test takes up to 30 minutes.<br />
                You can stop it from the guide anytime.
            </p>
            <button
                ref={skipRef}
                className="TsBanner__skip"
                onClick={handleSkip}
                disabled={isLoading}
            >
                {isLoading ? '…' : 'Skip'}
            </button>
        </div>
    );
}
